import React, { useState } from "react";
import { Copy, Check, Linkedin, Music2 } from "lucide-react";
import { toast } from "sonner";

const LI_TAGS = "#toxicboss #corporate #careeradvice #Leadership #WorkplaceCulture #HR #PsychologicalSafety #my360boss";
const TT_TAGS = "#toxicboss #corporate #careeradvice #worktok #corporatetiktok #toxicworkplace #careertok #my360boss";

export function supervisorCaptions(sup, url) {
  const otf = sup.traits?.open_to_feedback;
  const flag = sup.top_tags?.[0];
  return {
    linkedin: `${sup.name}, ${sup.title} at ${sup.company}, scores ${Number(sup.overall).toFixed(1)}/5 across ${sup.review_count} anonymous employee reviews on my360boss.\n\n` +
      `${otf ? `Open to Feedback: ${otf.toFixed(1)}/5 — the trait that predicts whether people can raise concerns safely.\n` : ""}` +
      `${sup.recommend_rate}% of reviewers would recommend working under them.${flag ? `\nMost-flagged behavior: ${flag.tag} (×${flag.count}).` : ""}\n\n` +
      `Every score is threshold-protected: nothing is shown until 5+ employees have reviewed, so no individual can be identified.\n\n${url}\n\n${LI_TAGS}`,
    tiktok: `Rate my boss: ${Number(sup.overall).toFixed(1)}/5 ⭐ from ${sup.review_count} anonymous reviews 👀\n` +
      `${flag ? `Biggest red flag: ${flag.tag} 🚩\n` : ""}${otf ? `Can you speak up without payback? ${otf.toFixed(1)}/5\n` : ""}` +
      `Review yours anonymously at my360boss.com — free forever.\n\n${TT_TAGS}`,
  };
}

export function companyCaptions(c, url) {
  const t = c.trajectory;
  return {
    linkedin: `How safe is it to speak up at ${c.name}?\n\n` +
      `Speak-Up score: ${c.speak_up_score ? c.speak_up_score.toFixed(1) : "—"}/5 ("I can raise concerns without it being held against me")\n` +
      `Org leadership score: ${c.org_score ? c.org_score.toFixed(1) : "—"}/5 across ${c.review_count} anonymous reviews of ${c.supervisor_count} managers\n` +
      `${t ? `Trajectory: ${t.improved_pct}% of reviewers say things have improved since their review.\n` : ""}` +
      `${c.top_tags?.[0] ? `Most-flagged behavior: ${c.top_tags[0].tag}.\n` : ""}\n` +
      `Data aggregated with a 5-review minimum per manager — anonymity by design.\n\n${url}\n\n${LI_TAGS}`,
    tiktok: `${c.name} speak-up score: ${c.speak_up_score ? c.speak_up_score.toFixed(1) : "—"}/5 🗣️\n` +
      `Leadership: ${c.org_score ? c.org_score.toFixed(1) : "—"}/5 · ${c.recommend_rate}% would recommend\n` +
      `${c.top_tags?.[0] ? `Top red flag: ${c.top_tags[0].tag} 🚩\n` : ""}` +
      `Would you work here? Anonymous reviews at my360boss.com\n\n${TT_TAGS}`,
  };
}

export function insightCaptions(data, flagText, url) {
  const top = data.trait_ranking[0];
  const bottom = data.trait_ranking[data.trait_ranking.length - 1];
  const name = (k) => k.replace(/_/g, " ");
  return {
    linkedin: `my360boss Macro Insights — ${data.industry}\n\n` +
      `Across ${data.review_count} anonymous employee reviews, managers score highest on "${name(top.key)}" (${top.avg}/5) and lowest on "${name(bottom.key)}" (${bottom.avg}/5).\n\n` +
      `Current workplace red flag: ${flagText}.\n\n` +
      `If the lowest-scoring trait in your organization is how safely people can raise concerns, that's not an HR problem — it's a risk problem.\n\n${url}\n\n${LI_TAGS} #StateOfManagement`,
    tiktok: `Managers are WORST at: ${name(bottom.key)} (${bottom.avg}/5) 😬\n` +
      `Best at: ${name(top.key)} (${top.avg}/5)\n` +
      `Red flag of the month: ${flagText} 🚩\n` +
      `Anonymous data from real employees → my360boss.com\n\n${TT_TAGS} #stateofmanagement`,
  };
}

export default function CaptionBox({ captions, testid = "captions", tab: tabProp, onTabChange }) {
  const [tabState, setTabState] = useState("linkedin");
  const tab = tabProp || tabState;
  const setTab = (t) => { setTabState(t); onTabChange?.(t); };
  const [copied, setCopied] = useState(false);
  if (!captions) return null;
  const text = captions[tab];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Caption copied — paste it into your post");
    } catch {
      window.prompt("Copy this caption:", text);
    }
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="w-full max-w-[420px] bg-slate-50 border border-slate-200 rounded-xl p-3" data-testid={testid}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex gap-1">
          {[["linkedin", "LinkedIn", Linkedin], ["tiktok", "TikTok", Music2]].map(([k, l, I]) => (
            <button key={k} type="button" data-testid={`${testid}-tab-${k}`} onClick={() => setTab(k)}
              className={`text-xs px-3 py-1.5 rounded-full border flex items-center gap-1.5 transition-colors ${tab === k ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-600 hover:border-slate-500"}`}>
              <I size={13} /> {l}
            </button>
          ))}
        </div>
        <button type="button" data-testid={`${testid}-copy`} onClick={copy} className="text-xs font-semibold text-emerald-700 hover:text-emerald-800 flex items-center gap-1">
          {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "Copied" : "Copy caption"}
        </button>
      </div>
      <textarea data-testid={`${testid}-text`} readOnly value={text} rows={7} className="w-full text-xs text-slate-700 bg-white border border-slate-200 rounded-lg p-2.5 leading-relaxed resize-none font-mono" />
    </div>
  );
}
